import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Edit, Trash2, Plus, ExternalLink, Users, Server, Database, 
  Link2, Globe, Mail, Phone, Building, User, CheckCircle, ArrowRight, ArrowLeftRight,
  Map
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOptions, setFormOptions] = useState({});
  const [activeTab, setActiveTab] = useState('owners');
  
  // Modals
  const [ownerModal, setOwnerModal] = useState({ open: false, data: {} });
  const [serverModal, setServerModal] = useState({ open: false, data: {} });
  const [dbModal, setDbModal] = useState({ open: false, data: {} });
  const [interfaceModal, setInterfaceModal] = useState({ open: false, data: {} });
  
  const { canUpdate, canDelete } = usePermissions('apps', 'applications');

  const fetchApp = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/applications/${id}`);
      setApp(response.data);
    } catch (error) {
      toast.error('Error al cargar la aplicación');
      navigate('/apps');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/applications/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  useEffect(() => {
    fetchApp();
    fetchFormOptions();
  }, [id]);

  // Owner handlers
  const handleAddOwner = async () => {
    if (!ownerModal.data.owner_name) {
      toast.error('Nombre es requerido');
      return;
    }
    try {
      await api.post(`/assets/applications/${id}/owners`, {
        ...ownerModal.data,
        role_id: ownerModal.data.role_id ? parseInt(ownerModal.data.role_id) : null,
        is_primary: ownerModal.data.is_primary ? 1 : 0,
      });
      toast.success('Responsable agregado');
      setOwnerModal({ open: false, data: {} });
      fetchApp();
    } catch (error) {
      toast.error('Error al agregar responsable');
    }
  };

  const handleRemoveOwner = async (ownerId) => {
    try {
      await api.delete(`/assets/applications/${id}/owners/${ownerId}`);
      toast.success('Responsable eliminado');
      fetchApp();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Server handlers
  const handleAddServer = async () => {
    if (!serverModal.data.server_id) {
      toast.error('Servidor es requerido');
      return;
    }
    try {
      await api.post(`/assets/applications/${id}/servers`, {
        ...serverModal.data,
        server_id: parseInt(serverModal.data.server_id),
      });
      toast.success('Servidor vinculado');
      setServerModal({ open: false, data: {} });
      fetchApp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular servidor');
    }
  };

  const handleRemoveServer = async (relationId) => {
    try {
      await api.delete(`/assets/applications/${id}/servers/${relationId}`);
      toast.success('Servidor desvinculado');
      fetchApp();
    } catch (error) {
      toast.error('Error al desvincular');
    }
  };

  // Database handlers
  const handleAddDatabase = async () => {
    if (!dbModal.data.db_name) {
      toast.error('Nombre de BD es requerido');
      return;
    }
    try {
      await api.post(`/assets/applications/${id}/databases`, {
        ...dbModal.data,
        sql_database_id: dbModal.data.sql_database_id ? parseInt(dbModal.data.sql_database_id) : null,
        db_type_id: dbModal.data.db_type_id ? parseInt(dbModal.data.db_type_id) : null,
        server_id: dbModal.data.server_id ? parseInt(dbModal.data.server_id) : null,
        is_primary: dbModal.data.is_primary ? 1 : 0,
      });
      toast.success('Base de datos agregada');
      setDbModal({ open: false, data: {} });
      fetchApp();
    } catch (error) {
      toast.error('Error al agregar BD');
    }
  };

  const handleRemoveDatabase = async (dbId) => {
    try {
      await api.delete(`/assets/applications/${id}/databases/${dbId}`);
      toast.success('Base de datos eliminada');
      fetchApp();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSqlDatabaseSelection = (value) => {
    const selectedDatabase = formOptions.sql_databases?.find(db => String(db.id) === value);

    setDbModal(previous => ({
      ...previous,
      data: {
        ...previous.data,
        sql_database_id: selectedDatabase ? value : null,
        db_name: selectedDatabase?.nombre || previous.data.db_name || '',
        server_id: selectedDatabase?.server_id || null,
      },
    }));
  };

  // Interface handlers
  const handleAddInterface = async () => {
    if (!interfaceModal.data.name) {
      toast.error('Nombre es requerido');
      return;
    }
    const externalSystemName = interfaceModal.data.external_system_name?.trim();
    if (!interfaceModal.data.target_app_id && !externalSystemName) {
      toast.error('Seleccioná una app destino o indicá un sistema externo');
      return;
    }
    try {
      await api.post(`/assets/applications/${id}/interfaces`, {
        ...interfaceModal.data,
        target_app_id: interfaceModal.data.target_app_id ? parseInt(interfaceModal.data.target_app_id) : null,
        interface_type_id: interfaceModal.data.interface_type_id ? parseInt(interfaceModal.data.interface_type_id) : null,
        criticity_id: interfaceModal.data.criticity_id ? parseInt(interfaceModal.data.criticity_id) : null,
        port: interfaceModal.data.port ? parseInt(interfaceModal.data.port) : null,
        external_system_name: externalSystemName || null,
      });
      toast.success('Interfaz agregada');
      setInterfaceModal({ open: false, data: {} });
      fetchApp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar interfaz');
    }
  };

  const handleRemoveInterface = async (interfaceId) => {
    try {
      await api.delete(`/assets/applications/${id}/interfaces/${interfaceId}`);
      toast.success('Interfaz eliminada');
      fetchApp();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </Layout>
    );
  }

  if (!app) return null;

  const getStatusBadge = (status, color) => (
    <span className="px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: color || '#6B7280' }}>
      {status || 'Sin estado'}
    </span>
  );

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case 'Inbound': return <ArrowLeft className="h-4 w-4 text-blue-500" strokeWidth={1.5} />;
      case 'Outbound': return <ArrowRight className="h-4 w-4 text-green-500" strokeWidth={1.5} />;
      case 'Bidirectional': return <ArrowLeftRight className="h-4 w-4 text-purple-500" strokeWidth={1.5} />;
      default: return <ArrowRight className="h-4 w-4" strokeWidth={1.5} />;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/apps')}>
              <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{app.code}</h1>
                {getStatusBadge(app.status_name, app.status_color)}
              </div>
              <p className="text-muted-foreground">{app.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/apps/map?focus=${app.id}`}>
              <Button variant="outline" className="gap-2">
                <Map className="h-4 w-4" strokeWidth={1.5} />
                Ver en Mapa
              </Button>
            </Link>
            {app.url_prod && (
              <a href={app.url_prod} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="gap-2">
                  <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                  Abrir App
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Info Card */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">{app.app_type_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Criticidad</p>
                <p className="font-medium">{app.criticity_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Versión</p>
                <p className="font-medium">{app.version || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Go-Live</p>
                <p className="font-medium">{app.go_live_date || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">Stack Tecnológico</p>
                <p className="font-medium">{app.technology_stack || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unidad de Negocio</p>
                <p className="font-medium">{app.business_unit || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Departamento</p>
                <p className="font-medium">{app.department || '-'}</p>
              </div>
            </div>
            {app.description && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">Descripción</p>
                <p className="mt-1">{app.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="owners" className="gap-2">
              <Users className="h-4 w-4" strokeWidth={1.5} />
              Responsables ({app.owners?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="servers" className="gap-2">
              <Server className="h-4 w-4" strokeWidth={1.5} />
              Servidores ({app.servers?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="databases" className="gap-2">
              <Database className="h-4 w-4" strokeWidth={1.5} />
              Bases de Datos ({app.databases?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="interfaces" className="gap-2">
              <Link2 className="h-4 w-4" strokeWidth={1.5} />
              Interfaces ({(app.interfaces_out?.length || 0) + (app.interfaces_in?.length || 0)})
            </TabsTrigger>
          </TabsList>

          {/* Owners Tab */}
          <TabsContent value="owners">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Responsables</CardTitle>
                {canUpdate && (
                  <Button size="sm" onClick={() => setOwnerModal({ open: true, data: {} })}>
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Agregar
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {app.owners?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No hay responsables asignados</p>
                ) : (
                  <div className="space-y-3">
                    {app.owners?.map((owner) => (
                      <div key={owner.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-primary/10">
                            <User className="h-4 w-4 text-primary" strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{owner.owner_name}</span>
                              {owner.is_primary === 1 && (
                                <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">Principal</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {owner.role_name && <span>{owner.role_name}</span>}
                              {owner.owner_email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" strokeWidth={1.5} /> {owner.owner_email}
                                </span>
                              )}
                              {owner.owner_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" strokeWidth={1.5} /> {owner.owner_phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {canUpdate && (
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRemoveOwner(owner.id)}>
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Servers Tab */}
          <TabsContent value="servers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Servidores Vinculados</CardTitle>
                {canUpdate && (
                  <Button size="sm" onClick={() => setServerModal({ open: true, data: { environment: 'Producción' } })}>
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Vincular
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {app.servers?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No hay servidores vinculados</p>
                ) : (
                  <div className="space-y-3">
                    {app.servers?.map((srv) => (
                      <div key={srv.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-green-500/10">
                            <Server className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <Link to={`/assets/servers/${srv.hostname}`} className="font-medium text-primary hover:underline">
                                {srv.hostname}
                              </Link>
                              <span className="px-2 py-0.5 bg-muted text-xs rounded">{srv.environment}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {srv.ip} {srv.role && `• ${srv.role}`}
                            </div>
                          </div>
                        </div>
                        {canUpdate && (
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRemoveServer(srv.id)}>
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Databases Tab */}
          <TabsContent value="databases">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Bases de Datos</CardTitle>
                {canUpdate && (
                  <Button size="sm" onClick={() => setDbModal({ open: true, data: { environment: 'Producción', access_type: 'Read/Write' } })}>
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Agregar
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {app.databases?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No hay bases de datos registradas</p>
                ) : (
                  <div className="space-y-3">
                    {app.databases?.map((db) => (
                      <div key={db.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-yellow-500/10">
                            <Database className="h-4 w-4 text-yellow-500" strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{db.db_name}</span>
                              {db.is_primary === 1 && (
                                <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">Principal</span>
                              )}
                              <span className="px-2 py-0.5 bg-muted text-xs rounded">{db.environment}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {db.db_type_name || 'SQL Server'} 
                              {db.server_hostname && ` • ${db.server_hostname}`}
                              {db.access_type && ` • ${db.access_type}`}
                            </div>
                          </div>
                        </div>
                        {canUpdate && (
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRemoveDatabase(db.id)}>
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interfaces Tab */}
          <TabsContent value="interfaces">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Interfaces / Integraciones</CardTitle>
                {canUpdate && (
                  <Button size="sm" onClick={() => setInterfaceModal({ open: true, data: { direction: 'Outbound' } })}>
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Agregar
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Outgoing */}
                {app.interfaces_out?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Salientes (esta app → destino)</h4>
                    <div className="space-y-3">
                      {app.interfaces_out?.map((iface) => (
                        <div key={iface.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getDirectionIcon(iface.direction)}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{iface.name}</span>
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded">
                                  {iface.interface_type_name || 'API'}
                                </span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                → {iface.target_app_id ? `${iface.target_app_code} (${iface.target_app_name})` : iface.external_system_name || 'Destino no definido'}
                                {iface.criticity_name && ` • ${iface.criticity_name}`}
                              </div>
                            </div>
                          </div>
                          {canUpdate && (
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRemoveInterface(iface.id)}>
                              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Incoming */}
                {app.interfaces_in?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Entrantes (origen → esta app)</h4>
                    <div className="space-y-3">
                      {app.interfaces_in?.map((iface) => (
                        <div key={iface.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <ArrowLeft className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{iface.name}</span>
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded">
                                  {iface.interface_type_name || 'API'}
                                </span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                ← {iface.source_app_code} ({iface.source_app_name})
                                {iface.criticity_name && ` • ${iface.criticity_name}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {app.interfaces_out?.length === 0 && app.interfaces_in?.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No hay interfaces registradas</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Owner Modal */}
        <Dialog open={ownerModal.open} onOpenChange={(open) => setOwnerModal({ open, data: {} })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Responsable</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input 
                  value={ownerModal.data.owner_name || ''} 
                  onChange={(e) => setOwnerModal(p => ({ ...p, data: { ...p.data, owner_name: e.target.value } }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select 
                  value={ownerModal.data.role_id || ''} 
                  onValueChange={(v) => setOwnerModal(p => ({ ...p, data: { ...p.data, role_id: v } }))}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.roles?.map(r => (
                      <SelectItem key={r.id} value={r.id.toString()}>{r.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    value={ownerModal.data.owner_email || ''} 
                    onChange={(e) => setOwnerModal(p => ({ ...p, data: { ...p.data, owner_email: e.target.value } }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input 
                    value={ownerModal.data.owner_phone || ''} 
                    onChange={(e) => setOwnerModal(p => ({ ...p, data: { ...p.data, owner_phone: e.target.value } }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOwnerModal({ open: false, data: {} })}>Cancelar</Button>
              <Button onClick={handleAddOwner}>Agregar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Server Modal */}
        <Dialog open={serverModal.open} onOpenChange={(open) => setServerModal({ open, data: {} })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Vincular Servidor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Servidor *</Label>
                <Select 
                  value={serverModal.data.server_id || ''} 
                  onValueChange={(v) => setServerModal(p => ({ ...p, data: { ...p.data, server_id: v } }))}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar servidor..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.servers?.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.nombre} ({s.ip})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ambiente</Label>
                  <Select 
                    value={serverModal.data.environment || 'Producción'} 
                    onValueChange={(v) => setServerModal(p => ({ ...p, data: { ...p.data, environment: v } }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Producción">Producción</SelectItem>
                      <SelectItem value="UAT">UAT</SelectItem>
                      <SelectItem value="Desarrollo">Desarrollo</SelectItem>
                      <SelectItem value="DR">DR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <Input 
                    value={serverModal.data.role || ''} 
                    onChange={(e) => setServerModal(p => ({ ...p, data: { ...p.data, role: e.target.value } }))}
                    placeholder="Web, App, DB..."
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setServerModal({ open: false, data: {} })}>Cancelar</Button>
              <Button onClick={handleAddServer}>Vincular</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Database Modal */}
        <Dialog open={dbModal.open} onOpenChange={(open) => setDbModal({ open, data: {} })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Base de Datos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                  <Label>Vincular BD SQL existente (opcional)</Label>
                  <Select
                    value={dbModal.data.sql_database_id?.toString() || 'none'}
                    onValueChange={(v) => handleSqlDatabaseSelection(v === 'none' ? null : v)}
                  >
                  <SelectTrigger><SelectValue placeholder="Seleccionar BD existente..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No vincular --</SelectItem>
                    {formOptions.sql_databases?.map(db => (
                      <SelectItem key={db.id} value={db.id.toString()}>{db.nombre} ({db.server_name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {dbModal.data.sql_database_id && (
                  <p className="text-xs text-muted-foreground">
                    El servidor anfitrión se vinculará automáticamente desde el inventario SQL.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre BD *</Label>
                  <Input 
                    value={dbModal.data.db_name || ''} 
                    onChange={(e) => setDbModal(p => ({ ...p, data: { ...p.data, db_name: e.target.value } }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de BD</Label>
                  <Select 
                    value={dbModal.data.db_type_id?.toString() || 'none'} 
                    onValueChange={(v) => setDbModal(p => ({ ...p, data: { ...p.data, db_type_id: v === 'none' ? null : v } }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ninguno --</SelectItem>
                      {formOptions.db_types?.map(t => (
                        <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ambiente</Label>
                  <Select 
                    value={dbModal.data.environment || 'Producción'} 
                    onValueChange={(v) => setDbModal(p => ({ ...p, data: { ...p.data, environment: v } }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Producción">Producción</SelectItem>
                      <SelectItem value="UAT">UAT</SelectItem>
                      <SelectItem value="Desarrollo">Desarrollo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo Acceso</Label>
                  <Select 
                    value={dbModal.data.access_type || 'Read/Write'} 
                    onValueChange={(v) => setDbModal(p => ({ ...p, data: { ...p.data, access_type: v } }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Read/Write">Read/Write</SelectItem>
                      <SelectItem value="Read Only">Read Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDbModal({ open: false, data: {} })}>Cancelar</Button>
              <Button onClick={handleAddDatabase}>Agregar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Interface Modal */}
        <Dialog open={interfaceModal.open} onOpenChange={(open) => setInterfaceModal({ open, data: {} })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Agregar Interfaz</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input 
                  value={interfaceModal.data.name || ''} 
                  onChange={(e) => setInterfaceModal(p => ({ ...p, data: { ...p.data, name: e.target.value } }))}
                  placeholder="API de Facturación"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo Interfaz</Label>
                  <Select 
                    value={interfaceModal.data.interface_type_id?.toString() || 'none'} 
                    onValueChange={(v) => setInterfaceModal(p => ({ ...p, data: { ...p.data, interface_type_id: v === 'none' ? null : v } }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ninguno --</SelectItem>
                      {formOptions.interface_types?.map(t => (
                        <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Select 
                    value={interfaceModal.data.direction || 'Outbound'} 
                    onValueChange={(v) => setInterfaceModal(p => ({ ...p, data: { ...p.data, direction: v } }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Outbound">Saliente</SelectItem>
                      <SelectItem value="Inbound">Entrante</SelectItem>
                      <SelectItem value="Bidirectional">Bidireccional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                  <Label>App Destino (si es interna)</Label>
                <Select 
                  value={interfaceModal.data.target_app_id || 'none'} 
                  onValueChange={(v) => setInterfaceModal(p => ({ ...p, data: { ...p.data, target_app_id: v === 'none' ? '' : v } }))}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar app..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Seleccionar sistema externo abajo --</SelectItem>
                    {formOptions.applications?.filter(a => a.id !== parseInt(id)).map(a => (
                      <SelectItem key={a.id} value={a.id.toString()}>{a.code} - {a.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sistema Externo (si no es app interna)</Label>
                <Input 
                  value={interfaceModal.data.external_system_name || ''} 
                  onChange={(e) => setInterfaceModal(p => ({ ...p, data: { ...p.data, external_system_name: e.target.value } }))}
                  placeholder="SAP, SUNAT, Banco..."
                />
                <p className="text-xs text-muted-foreground">Requerido: seleccioná una app destino o indicá un sistema externo.</p>
              </div>
              <div className="space-y-2">
                <Label>Criticidad</Label>
                <Select 
                  value={interfaceModal.data.criticity_id?.toString() || 'none'} 
                  onValueChange={(v) => setInterfaceModal(p => ({ ...p, data: { ...p.data, criticity_id: v === 'none' ? null : v } }))}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Ninguna --</SelectItem>
                    {formOptions.criticities?.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInterfaceModal({ open: false, data: {} })}>Cancelar</Button>
              <Button onClick={handleAddInterface}>Agregar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ApplicationDetail;
